import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Crown, Loader2 } from "lucide-react";
import PlanPicker from "@/components/billing/PlanPicker";
import PremiumStatus from "@/components/billing/PremiumStatus";
import PaymentHistory from "@/components/billing/PaymentHistory";

const POLL_MS = 2000;
const MAX_POLLS = 15;

export default function Billing({ onBack, sessionId, onSessionHandled }) {
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(!!sessionId);

  const load = useCallback(() => api.get("/payments/billing").then(({ data }) => setData(data)).catch(() => toast.error("Couldn't load billing")), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0, cancelled = false;
    const poll = async () => {
      try {
        const { data: s } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (s.payment_status === "paid") {
          toast.success("Welcome to Finlo Premium!");
          await Promise.all([load(), refreshUser()]);
          return finish();
        }
        if (s.status === "expired" || s.payment_status === "failed") {
          toast.error("Payment didn't go through. You haven't been charged.");
          return finish();
        }
      } catch { /* retry */ }
      if (++tries >= MAX_POLLS) { toast.info("Payment is still processing. We'll update your plan as soon as it's confirmed."); return finish(); }
      setTimeout(poll, POLL_MS);
    };
    const finish = () => { setChecking(false); onSessionHandled?.(); };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const run = async (path, msg) => {
    try {
      const { data: r } = await api.post(path);
      setData((d) => ({ ...d, premium: r.premium }));
      refreshUser();
      toast.success(msg);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="mm-fade-up max-w-4xl" data-testid="billing-page">
      <button data-testid="billing-back-button" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-5">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>
      <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#00C896]">Premium & billing</p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 flex items-center gap-2.5">
        <Crown className="h-8 w-8 text-[#00C896]" /> Finlo Premium
      </h1>
      <p className="text-slate-400 text-sm mt-2 max-w-xl">Support Finlo and get first access to every new premium tool as it launches.</p>

      {checking && (
        <div data-testid="billing-confirming" className="mt-6 mm-glow-card p-5 flex items-center gap-3 text-sm text-slate-300">
          <Loader2 className="h-5 w-5 animate-spin text-[#00C896]" /> Confirming your payment with Stripe…
        </div>
      )}

      {!data ? (
        <div className="h-48 flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#00C896]" /></div>
      ) : (
        <div className="mt-6 space-y-5">
          {data.premium.active
            ? <PremiumStatus premium={data.premium} onCancel={() => run("/payments/cancel", "Premium will end at the end of this period")} onResume={() => run("/payments/resume", "Premium will keep renewing")} />
            : !checking && <PlanPicker />}
          <PaymentHistory history={data.history} />
        </div>
      )}
    </div>
  );
}
